import React from "react";
import {observer} from "mobx-react-lite";
import {Alert, Badge, Button, CircularProgress, Stack} from "@mui/material";
import {FactCheckOutlined} from "@mui/icons-material";
import {ReactFlowProvider} from "@xyflow/react";
import {generatePath, useNavigate} from "react-router";
import {routes} from "~/routing/routes";
import {useStores} from "~/stores";
import {MainPageFacade} from "~/components/main/page/MainPageFacade.tsx";
import {TaxonomyCanvas} from "~/prototypes/pages/taxonomy-canvas/TaxonomyCanvas.tsx";
import {taxonomyCanvasEditsStore} from "~/prototypes/pages/taxonomy-canvas/taxonomyCanvasEditsStore.ts";

/**
 * PROTOTYPE: taxonomy tree on a pannable / zoomable canvas (React Flow).
 * Loads the databag's taxonomy through the same store as the Taxonomy Builder. Edits stay in memory;
 * the button top-right shows how many are pending and opens the approval page.
 */
export const TaxonomyCanvasPage: React.FC = observer(() => {
    const {taxonomyManagerStore, bagStore} = useStores();
    const navigate = useNavigate();
    const taxonomy = taxonomyManagerStore.taxonomy;
    const nPending = taxonomy ? taxonomyCanvasEditsStore.get(taxonomy.id).pending.length : 0;

    return <MainPageFacade
        title="Taxonomy Canvas"
        titleTag="Prototype"
        actionBarEnd={taxonomy && <Badge badgeContent={nPending} color="error" max={99}>
            <Button
                variant="outlined"
                startIcon={<FactCheckOutlined/>}
                onClick={() => navigate(generatePath(routes.prototype_taxonomy_canvas_approval, {id: String(bagStore.bagId)}))}
            >
                Review changes
            </Button>
        </Badge>}
    >
        {taxonomy === undefined && !taxonomyManagerStore.error &&
            <Stack alignItems="center" sx={{py: 6}}><CircularProgress/></Stack>}
        {taxonomyManagerStore.error && <Alert severity="error">Could not load the taxonomy.</Alert>}
        {taxonomy === null && <Alert severity="info">This dataset has no taxonomy.</Alert>}
        {taxonomy && <ReactFlowProvider key={taxonomy.id}>
            <TaxonomyCanvas taxonomyId={taxonomy.id} tree={taxonomy.diff_tree} name={taxonomy.name}/>
        </ReactFlowProvider>}
    </MainPageFacade>
});
